import React, { useEffect, useState } from 'react';
import { Switch, Alert, Spin, Tag, Empty, InputNumber, Button } from 'antd';
import { SafetyOutlined, BankOutlined, LockOutlined, MobileOutlined, FieldTimeOutlined, DesktopOutlined, MailOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../i18n';
import { getUser, can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';

// Le BNCB = bureau national de la Carte Brune du Togo.
const estBncbTogo = (o) => o?.typeOrganisme === 'BUREAU_NATIONAL'
  && (o?.codePays === 'TGO' || o?.codePaysBCB === 'TG');

export default function ParametresSecurite() {
  const { t } = useTranslation('parametres');
  const user = getUser();
  /* DEUX MOITIÉS, DEUX HABILITATIONS. Cet écran écrit dans deux endroits que le
     serveur ne garde pas pareil : les interrupteurs passent par
     PUT /v1/organismes/{id}/two-factor (ORGANISME_UPDATE), les durées de validité
     par POST/PUT /v1/parametres (PARAMETRE_CREATE / PARAMETRE_UPDATE). Les fondre
     en un seul « habilité » laisserait cliquer un réglage que le serveur refuse
     ensuite - un 403 pour toute explication.

     Ces gardes remplacent un test sur le NOM du profil (ADMIN / SE), qui excluait
     le SUPERADMIN de son propre écran de sécurité alors qu'il peut tout le reste. */
  const peutOrganisme = can(HAB.ORGANISME_UPDATE, user);
  const peutParametres = can(HAB.PARAMETRE_UPDATE, user) && can(HAB.PARAMETRE_CREATE, user);
  const habilite = peutOrganisme || peutParametres;

  const [org, setOrg] = useState(null);       // organisme BNCB
  /* Deux canaux, deux réglages. Un seul interrupteur commandait le back-office
     ET l'application mobile : un bureau ne pouvait pas exiger le code sur le
     téléphone - le canal le plus exposé aux pertes et aux vols - sans l'imposer
     aussi au poste de bureau. `enabled` garde son nom et désigne le WEB. */
  const [enabled, setEnabled] = useState(false);
  const [mobile, setMobile] = useState(false);
  // Voie de secours par application d'authentification. Distincte du 2FA lui-même :
  // un bureau peut exiger le second facteur tout en imposant qu'il passe par courriel.
  const [authenticatorAutorise, setAuthenticatorAutorise] = useState(true);
  /* Le serveur sait-il faire du TOTP ? Sans TOTP_SECRET_KEY dans son environnement,
     la fonctionnalite est desactivee EN SILENCE : l'interrupteur ci-dessous
     afficherait « Autorisee » pour quelque chose que le serveur ne peut pas rendre,
     et personne ne verrait jamais le QR d'association sans comprendre pourquoi. */
  const [totpDisponible, setTotpDisponible] = useState(true);

  /* Courriel adresse a l'agent qui recoit un dossier. Reglage du bureau, non de
     l'agent : c'est une notification de travail, dont on ne se retire pas seul. */
  const [affectationMail, setAffectationMail] = useState(true);

  /* Durée de validité des codes OTP. Deux canaux, deux réglages : l'usager mobile
     reçoit son code sur un téléphone, souvent en mobilité - la fenêtre confortable
     n'y est pas la même qu'au back-office. Le serveur borne à 1-60 minutes et
     retombe sur son défaut si la valeur est illisible. */
  const [otpParams, setOtpParams] = useState({});
  const [otpWeb, setOtpWeb] = useState(5);
  const [otpMobile, setOtpMobile] = useState(10);
  const [savingOtp, setSavingOtp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/v1/parametres');
        const tous = res?.data?.data || [];
        const trouver = (cle) => tous.find((p) => p.cle === cle) || null;
        const web = trouver('OTP_VALIDITE_MINUTES_WEB');
        const mobile = trouver('OTP_VALIDITE_MINUTES_MOBILE');
        if (ignore) return;
        setOtpParams({ OTP_VALIDITE_MINUTES_WEB: web, OTP_VALIDITE_MINUTES_MOBILE: mobile });
        if (web?.valeur) setOtpWeb(Number(web.valeur));
        if (mobile?.valeur) setOtpMobile(Number(mobile.valeur));
      } catch {
        // Silencieux : l'absence de ces paramètres n'empêche pas la page de servir
        // les deux interrupteurs, et le serveur a de toute façon ses valeurs de repli.
      }
    })();
    return () => { ignore = true; };
  }, []);

  const enregistrerOtp = async () => {
    if (!peutParametres) return;
    setSavingOtp(true);
    try {
      const aEcrire = [
        {
          cle: 'OTP_VALIDITE_MINUTES_WEB', valeur: String(otpWeb),
          description: 'Duree de validite du code OTP de connexion au back-office (minutes, 1 a 60)'
        },
        {
          cle: 'OTP_VALIDITE_MINUTES_MOBILE', valeur: String(otpMobile),
          description: 'Duree de validite du code OTP envoye aux usagers du canal mobile (minutes, 1 a 60)'
        },
      ];
      await Promise.all(aEcrire.map(({ cle, valeur, description }) => {
        const payload = { typeParametre: 'SYSTEM', cle, valeur, description };
        const existant = otpParams[cle];
        return existant
          ? axiosInstance.put(`/v1/parametres/${existant.parametreTrackingId}`, payload)
          : axiosInstance.post('/v1/parametres', payload);
      }));
      toast.success(t('securite.messages.dureesEnregistrees'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('securite.messages.echecEnregistrement'));
    } finally {
      setSavingOtp(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        /* `skipErrorToast` et pas seulement le .catch() : l'intercepteur global
           toaste toute erreur portant un message AVANT que ce catch ne s'execute.
           L'ecran survivait donc, mais en jetant le message technique du serveur
           a la figure de l'utilisateur - c'est ainsi qu'un NullPointerException
           s'est affiche a l'ouverture de cet ecran. Cet appel ne renseigne qu'un
           avertissement : son echec doit etre silencieux. */
        axiosInstance.get('/v1/compte/authenticator/etat', { skipErrorToast: true })
          .then((r) => { if (!ignore) setTotpDisponible(r.data?.data?.disponible !== false); })
          .catch(() => { /* l'avertissement ne doit pas empecher l'ecran de s'afficher */ });

        const res = await axiosInstance.get('/v1/organismes/type/BUREAU_NATIONAL');
        const list = res.data?.data || [];
        const bncb = list.find(estBncbTogo) || list[0] || null;
        if (!ignore) {
          setOrg(bncb);
          setEnabled(!!bncb?.twoFactorEnabled);
          setMobile(!!bncb?.twoFactorMobile);
          setAuthenticatorAutorise(bncb?.authenticatorAutorise !== false);
          setAffectationMail(bncb?.affectationMailActive !== false);
        }
      } catch {
        if (!ignore) toast.error(i18n.t('parametres:securite.messages.erreurChargementBncb'));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  /* `mobile` n'est PAS envoyé ici : le serveur lit son absence comme « ne
     touche pas ». Régler le web ne doit jamais, par effet de bord, couper le
     code sur le mobile. */
  const toggle = async (val) => {
    if (!org || !peutOrganisme) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/v1/organismes/${org.organismeTrackingId}/two-factor`, {
        enabled: val, authenticatorAutorise,
      });
      setEnabled(val);
      toast.success(val
        ? t('securite.messages.webExige')
        : t('securite.messages.webLeve'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('securite.messages.echecDoubleAuthentification'));
    } finally {
      setSaving(false);
    }
  };

  const toggleMobile = async (val) => {
    if (!org || !peutOrganisme) return;
    setSaving(true);
    try {
      // `enabled` est obligatoire côté serveur : on renvoie sa valeur ACTUELLE,
      // pour que régler le mobile laisse le web tel quel.
      await axiosInstance.put(`/v1/organismes/${org.organismeTrackingId}/two-factor`, {
        enabled, authenticatorAutorise, mobile: val,
      });
      setMobile(val);
      toast.success(val
        ? t('securite.messages.mobileExige')
        : t('securite.messages.mobileLeve'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('securite.messages.echecMiseAJour'));
    } finally {
      setSaving(false);
    }
  };

  const toggleAuthenticator = async (val) => {
    if (!org || !peutOrganisme) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/v1/organismes/${org.organismeTrackingId}/two-factor`, {
        enabled, authenticatorAutorise: val,
      });
      setAuthenticatorAutorise(val);
      toast.success(val
        ? t('securite.messages.authenticatorAutorise')
        : t('securite.messages.authenticatorInterdit'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('securite.messages.echecMiseAJour'));
    } finally {
      setSaving(false);
    }
  };

  /* Route distincte de `two-factor` : couper un courriel ne touche pas a qui a le
     droit d'entrer, et melanger les deux dans une route nommee « two-factor »
     aurait rendu illisible ce qu'un administrateur modifie en la sollicitant. */
  const toggleAffectationMail = async (val) => {
    if (!org || !peutOrganisme) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/v1/organismes/${org.organismeTrackingId}/notifications`, {
        affectationMail: val,
      });
      setAffectationMail(val);
      toast.success(val
        ? t('securite.messages.affectationActive')
        : t('securite.messages.affectationCoupee'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('securite.messages.echecMiseAJour'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={t('securite.page.titreOnglet')}>
      <div className="cb-page-header">
        <div>
          <h1 className="cb-page-title">
            <SafetyOutlined style={{ color: '#1a6b3c', marginRight: 8 }} />
            {t('securite.page.titre')}
          </h1>
        </div>
      </div>

      {/* Dire LEQUEL des deux réglages est hors de portée : « réservé aux
          administrateurs » n'apprend rien à qui en tient déjà la moitié. */}
      {!habilite && (
        <Alert
          type="warning"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={t('securite.alertes.aucuneHabilitation')}
        />
      )}
      {habilite && !peutOrganisme && (
        <Alert
          type="warning"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={t('securite.alertes.sansOrganisme')}
        />
      )}
      {habilite && !peutParametres && (
        <Alert
          type="warning"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={t('securite.alertes.sansParametres')}
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 10 }}
        message={t('securite.alertes.information')}
      />

      {loading ? (
        <div className="cb-card" style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : !org ? (
        <div className="cb-card">
          <Empty description={t('securite.bncb.introuvable')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <div className="cb-card" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <BankOutlined style={{ color: '#1a6b3c', fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{org.raisonSociale || t('securite.bncb.nomParDefaut')}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                {t('securite.canaux.description')}
              </div>
            </div>
          </div>
          {/* Une ligne par canal, chacune avec son état ET son interrupteur :
              deux interrupteurs côte à côte sans libellé laisseraient deviner
              lequel commande quoi. */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              {
                cle: 'web', icone: <DesktopOutlined />, libelle: t('securite.canaux.webLibelle'),
                detail: t('securite.canaux.webDetail'),
                valeur: enabled, onChange: toggle,
              },
              {
                cle: 'mobile', icone: <MobileOutlined />, libelle: t('securite.canaux.mobileLibelle'),
                detail: t('securite.canaux.mobileDetail'),
                valeur: mobile, onChange: toggleMobile,
              },
            ].map((c) => (
              <div key={c.cle} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#1a6b3c', fontSize: 18, display: 'inline-flex' }}>{c.icone}</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: 13.5 }}>
                      {c.libelle}{' '}
                      {c.valeur
                        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('securite.canaux.codeExige')}</Tag>
                        : <Tag style={{ borderRadius: 20 }}>{t('securite.canaux.desactive')}</Tag>}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{c.detail}</div>
                  </div>
                </div>
                <Switch
                  checked={c.valeur}
                  disabled={!peutOrganisme}
                  loading={saving}
                  onChange={c.onChange}
                  checkedChildren={t('securite.canaux.interrupteurActive')}
                  unCheckedChildren={t('securite.canaux.interrupteurDesactive')}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Un interrupteur « Autorisée » ne vaut rien si le serveur ne peut pas faire
          de TOTP : le reglage serait vrai en base et sans effet, et l'agent
          chercherait un QR qui ne viendra jamais. On le dit ici. */}
      {!loading && org && (enabled || mobile) && !totpDisponible && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={t('securite.totp.titre')}
          description={
            <>
              <Trans t={t} i18nKey="securite.totp.description" components={{ 1: <strong />, 3: <code /> }} />
            </>
          }
        />
      )}

      {/* Sans 2FA actif, ce réglage n'a rien à gouverner : on ne l'affiche pas. */}
      {/* Commune aux deux canaux : c'est la même application, sur le même
          téléphone de l'agent. Elle s'affiche dès qu'un canal exige le code. */}
      {!loading && org && (enabled || mobile) && (
        <div className="cb-card" style={{
          padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginTop: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <MobileOutlined style={{ color: '#1a6b3c', fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{t('securite.authenticator.titre')}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 520 }}>
                {t('securite.authenticator.description')}
              </div>
            </div>
          </div>
          <Switch
            checked={authenticatorAutorise}
            disabled={!peutOrganisme}
            loading={saving}
            onChange={toggleAuthenticator}
            checkedChildren={t('securite.authenticator.autorisee')}
            unCheckedChildren={t('securite.authenticator.interdite')}
          />
        </div>
      )}

      {/* Courriel d'affectation. Ce n'est pas un réglage de sécurité, mais il vit
          ici parce que c'est la page des politiques du bureau : un interrupteur
          d'organisme, que l'agent ne peut pas lever de son côté. */}
      {!loading && (
        <div className="cb-card" style={{
          padding: 20, marginTop: 12, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <MailOutlined style={{ color: '#1a6b3c', fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>
                {t('securite.affectation.titre')}
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 560 }}>
                {t('securite.affectation.description')}
              </div>
            </div>
          </div>
          <Switch
            checked={affectationMail}
            disabled={!peutOrganisme}
            loading={saving}
            onChange={toggleAffectationMail}
            checkedChildren={t('securite.affectation.envoye')}
            unCheckedChildren={t('securite.affectation.coupe')}
          />
        </div>
      )}

      {/* Durée de validité des codes, indépendante de l'activation du 2FA :
          le canal mobile envoie des codes même quand le back-office ne l'exige pas. */}
      {!loading && (
        <div className="cb-card" style={{ padding: 20, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FieldTimeOutlined style={{ color: '#1a6b3c', fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{t('securite.otp.titre')}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 560 }}>
                {t('securite.otp.description')}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 500 }}>
                {t('securite.otp.web')}
              </span>
              <InputNumber
                min={1} max={60} value={otpWeb} onChange={(v) => setOtpWeb(v ?? 5)}
                disabled={!peutParametres} addonAfter="min" style={{ width: 130 }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 500 }}>
                {t('securite.otp.mobile')}
              </span>
              <InputNumber
                min={1} max={60} value={otpMobile} onChange={(v) => setOtpMobile(v ?? 10)}
                disabled={!peutParametres} addonAfter="min" style={{ width: 130 }}
              />
            </label>

            <Button
              type="primary" loading={savingOtp} disabled={!peutParametres}
              onClick={enregistrerOtp} style={{ background: '#1a6b3c' }}
            >
              {t('securite.otp.enregistrer')}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
